import { TerritoryBoundary } from './typings'

/** A point in geographic space, in GeoJSON's [longitude, latitude] order. */
export type LngLat = [number, number]

/**
 * Turning a freehand drag into a valid territory polygon (WWM-17508).
 *
 * A drag emits a pointermove roughly every frame, so a few seconds of drawing is easily 500-2000
 * positions, most of them a fraction of a metre apart. Sent as-is that is a ~100 KB boundary that
 * renders slowly and tells the server nothing a far smaller ring would not. Everything here exists
 * to get from that raw trail to a ring the server will accept.
 *
 * The server is the real validator -- TerritoryGeometry.TryBuild rejects self-intersection,
 * out-of-range coordinates, fewer than 3 distinct vertices and more than 10,000 of them. These
 * helpers are not a second implementation of that. They exist so the common case arrives clean,
 * and so the user is told "that shape crosses itself" by a round trip rather than by a 500.
 */

/**
 * Simplification tolerance in degrees. ~1e-5 degrees is roughly a metre of latitude, so 2e-5 is
 * about two metres -- far below anything a user can perceive when drawing a neighbourhood
 * boundary, while typically cutting the vertex count by an order of magnitude.
 */
export const SIMPLIFY_TOLERANCE = 2e-5

/**
 * Hard ceiling on ring size. The server rejects above 10,000; stopping well short keeps the
 * payload small and leaves headroom, since simplification is tolerance-based and cannot promise
 * a specific count on a pathological path (a very long, very wiggly drag).
 */
export const MAX_VERTICES = 2000

/**
 * How much of a freehand stroke's tail may be given up to make the loop close cleanly.
 *
 * Finishing a lasso is the hardest part of drawing one: you come back round and sail PAST where
 * you started, and that overshoot makes the closing run cut across your own first leg -- a
 * self-intersection, which used to be rejected with "draw a loop that finishes near where it
 * started". The shape was fine; only the last few millimetres of the gesture were not.
 *
 * So the tail is trimmed back until the ring is simple, up to this many positions. These are
 * POST-simplification positions, where a real drawing runs to a few dozen, so this is a small
 * fraction of the stroke -- enough to cover an overshoot, not enough to quietly redraw someone's
 * territory into a different shape. A figure-of-eight still fails, and should.
 */
export const MAX_TAIL_TRIM = 12

/**
 * How much of the stroke's LENGTH the trim may give back.
 *
 * Measured in length rather than positions, because simplification collapses a long freehand
 * stroke to a handful of positions -- a counted budget reads a 400-point drag as "short" and
 * refuses to fix it. Length survives that: an overshoot is a few metres on the end of a stroke
 * hundreds of metres round, while a lobe of a figure-of-eight is a third of it. So this is what
 * separates correcting a slip from reinterpreting a shape somebody meant to draw.
 */
export const MAX_TAIL_TRIM_FRACTION = 0.1

/** Plain planar length. Degrees are fine: every use here is a ratio against another length. */
function pathLength(points: LngLat[]): number {
  let total = 0

  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i - 1]
    const [x2, y2] = points[i]
    total += Math.hypot(x2 - x1, y2 - y1)
  }

  return total
}

/** Perpendicular distance from p to the infinite line through a and b, in degrees. */
function perpendicularDistance(p: LngLat, a: LngLat, b: LngLat): number {
  const [px, py] = p
  const [ax, ay] = a
  const [bx, by] = b

  const dx = bx - ax
  const dy = by - ay

  // Degenerate segment: a and b are the same point, so fall back to point-to-point distance.
  if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay)

  return Math.abs(dy * px - dx * py + bx * ay - by * ax) / Math.hypot(dx, dy)
}

/**
 * Douglas-Peucker, iteratively rather than recursively.
 *
 * Recursion is the usual formulation and reads better, but the input here is user-controlled and
 * can be thousands of points; a deep or adversarial path risks blowing the call stack and taking
 * the map down with it. An explicit stack cannot.
 */
export function simplifyPath(
  points: LngLat[],
  tolerance: number = SIMPLIFY_TOLERANCE,
): LngLat[] {
  if (points.length <= 2) return [...points]

  const keep = new Array<boolean>(points.length).fill(false)
  keep[0] = true
  keep[points.length - 1] = true

  const stack: Array<[number, number]> = [[0, points.length - 1]]

  while (stack.length) {
    const [first, last] = stack.pop()!
    if (last <= first + 1) continue

    let maxDistance = 0
    let index = first

    for (let i = first + 1; i < last; i++) {
      const distance = perpendicularDistance(
        points[i],
        points[first],
        points[last],
      )
      if (distance > maxDistance) {
        maxDistance = distance
        index = i
      }
    }

    if (maxDistance > tolerance) {
      keep[index] = true
      stack.push([first, index], [index, last])
    }
  }

  return points.filter((_, i) => keep[i])
}

/**
 * Drop consecutive duplicates. Pointer events repeat the same position when the pointer is held
 * still, and a ring whose vertices repeat contributes nothing but is counted toward the server's
 * "at least 3 distinct vertices" check.
 */
export function dropConsecutiveDuplicates(points: LngLat[]): LngLat[] {
  return points.filter(
    (p, i) => i === 0 || p[0] !== points[i - 1][0] || p[1] !== points[i - 1][1],
  )
}

/**
 * Thin an over-long ring by keeping every nth vertex, preserving the first and last.
 *
 * Only reached when simplification alone could not get under MAX_VERTICES, which takes a
 * genuinely enormous drag. Crude on purpose: at that size the shape is dominated by its overall
 * form, not by any individual vertex.
 */
function thinToLimit(points: LngLat[], limit: number): LngLat[] {
  if (points.length <= limit) return points

  const step = Math.ceil(points.length / limit)
  const thinned = points.filter((_, i) => i % step === 0)

  const last = points[points.length - 1]
  const tail = thinned[thinned.length - 1]
  if (tail[0] !== last[0] || tail[1] !== last[1]) thinned.push(last)

  return thinned
}

/**
 * Close a ring by repeating the first position as the last, which GeoJSON requires.
 *
 * The server auto-closes too, so this is belt and braces -- but it also means the preview layer
 * and the saved geometry are the same shape, rather than the preview being subtly open.
 */
export function closeRing(points: LngLat[]): LngLat[] {
  if (points.length < 3) return points

  const first = points[0]
  const last = points[points.length - 1]
  if (first[0] === last[0] && first[1] === last[1]) return points

  return [...points, [first[0], first[1]]]
}

/**
 * Largest ring the client will test for self-intersection.
 *
 * The test is O(n^2) in segments. After simplification a real drawing is typically well under a
 * hundred vertices, so this is microseconds; the cap only stops a pathological scribble from
 * janking the map. Above it the server still rejects the shape -- the client check is a fast
 * path, never the authority.
 */
const MAX_VERTICES_FOR_INTERSECTION_TEST = 600

/** Sign of the cross product (a->b) x (a->c); 0 when the three points are collinear. */
function orientation(a: LngLat, b: LngLat, c: LngLat): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
}

/**
 * Proper intersection of two open segments.
 *
 * Deliberately strict: shared endpoints and collinear touching do NOT count. Adjacent segments
 * in a ring always share an endpoint, and a freehand path frequently doubles back onto itself
 * for a moment without producing a polygon the server would reject.
 */
function segmentsCross(
  p1: LngLat,
  p2: LngLat,
  p3: LngLat,
  p4: LngLat,
): boolean {
  const d1 = orientation(p3, p4, p1)
  const d2 = orientation(p3, p4, p2)
  const d3 = orientation(p1, p2, p3)
  const d4 = orientation(p1, p2, p4)

  // Strictly opposite sides. A zero -- an endpoint sitting exactly ON the other segment's line
  // -- is touching, not crossing, and that is what the summary above promises.
  //
  // Zero must be excluded explicitly rather than folded in with "negative", or the answer
  // depends on which side the OTHER end happens to fall. That is not a corner case here:
  // unproject returns identical latitudes right across a row of pixels, so a horizontal drag
  // puts a vertex exactly on an earlier segment's line constantly.
  const strictlyOpposite = (a: number, b: number) =>
    (a > 0 && b < 0) || (a < 0 && b > 0)

  return strictlyOpposite(d1, d2) && strictlyOpposite(d3, d4)
}

/**
 * Does this closed ring cross itself?
 *
 * Catches the common case of what NetTopologySuite's IsValidOp rejects server-side, so the user
 * usually finds out while still holding the pen rather than after a round trip and a modal.
 *
 * NOT equivalent to IsValidOp, and must not be described as if it were. segmentsCross is
 * strict, so two things it calls "not crossing" are rejected by the server: segments that
 * overlap collinearly, and a vertex landing exactly on another segment. A stroke that doubles
 * back along one row of pixels produces precisely that -- unproject gives identical latitudes
 * across a row -- so such a shape passes here, the admin names it, and the save comes back 400.
 * Treat a server rejection after a clean check as expected, not as a bug in one of the two.
 *
 * In practice the offending segment is almost always the CLOSING one: a freehand lasso that
 * stops far from where it started gets a long straight run home, and that run cuts back across
 * the drawn path. That is the case this was written for.
 */
export function hasSelfIntersection(ring: LngLat[]): boolean {
  const segmentCount = ring.length - 1
  if (segmentCount < 4) return false
  if (ring.length > MAX_VERTICES_FOR_INTERSECTION_TEST) return false

  for (let i = 0; i < segmentCount; i++) {
    for (let j = i + 2; j < segmentCount; j++) {
      // The first and last segments are adjacent through the closing point.
      if (i === 0 && j === segmentCount - 1) continue

      if (segmentsCross(ring[i], ring[i + 1], ring[j], ring[j + 1])) return true
    }
  }

  return false
}

/**
 * Gives back the tail, one position at a time, until the ring no longer crosses itself.
 *
 * Trims the END rather than the start because that is where the slip is: the overshoot is the
 * last thing drawn, and the closing run is what it breaks. Dropping those positions leaves the
 * loop the user actually traced.
 *
 * Returns null when no trim within budget yields a simple ring -- a shape that crosses itself
 * somewhere in the middle, which no amount of tail-trimming can rescue and which the caller
 * should still reject.
 *
 * Bounded by LENGTH as well as by a position count, and that is the important part. Without it,
 * trimming is free to reinterpret a deliberate shape instead of correcting a slip: a
 * four-position figure-of-eight becomes a clean triangle by dropping one position, and the user
 * is handed a territory they never drew. Asking them to draw it again is the honest answer.
 */
export function trimTailToSimpleRing(points: LngLat[]): LngLat[] | null {
  const lengthBudget = pathLength(points) * MAX_TAIL_TRIM_FRACTION

  let remaining = points
  let givenBack = 0

  for (let dropped = 0; dropped <= MAX_TAIL_TRIM; dropped++) {
    const ring = closeRing(thinToLimit(remaining, MAX_VERTICES))

    if (ring.length >= 4 && !hasSelfIntersection(ring)) return ring

    // Below a triangle there is no area left to save.
    if (remaining.length <= 3) return null

    const [x1, y1] = remaining[remaining.length - 2]
    const [x2, y2] = remaining[remaining.length - 1]
    givenBack += Math.hypot(x2 - x1, y2 - y1)

    // Spending more than the budget would mean rebuilding the shape, not repairing it.
    if (givenBack > lengthBudget) return null

    remaining = remaining.slice(0, -1)
  }

  return null
}

/**
 * The same cleanup as a territory, but left OPEN -- a line that divides, not a shape.
 *
 * Simplified the same way and for the same reason: a drag emits a position per frame, and the
 * server has to node this against the territory's boundary, which is work proportional to the
 * number of segments.
 *
 * Returns null for anything that is not a line. Two points that survive simplification is the
 * minimum, and a tap produces one.
 */
export function pathToLine(
  rawPoints: LngLat[],
  tolerance: number = SIMPLIFY_TOLERANCE,
): LngLat[] | null {
  const deduped = dropConsecutiveDuplicates(rawPoints)
  if (deduped.length < 2) return null

  const simplified = dropConsecutiveDuplicates(simplifyPath(deduped, tolerance))
  if (simplified.length < 2) return null

  // Not closed, and the vertex ceiling still applies: the server rejects an over-long cut the
  // same way it rejects an over-detailed boundary.
  return thinToLimit(simplified, MAX_VERTICES)
}

/**
 * Why a drawing could not become a territory.
 *
 * 'too-small' is a tap or a flick -- the user almost certainly did not mean to draw, so the
 * caller clears quietly. 'crosses-itself' is a real attempt that needs explaining.
 */
export type LassoRejection = 'too-small' | 'crosses-itself'

export type LassoResult =
  | { ok: true; boundary: TerritoryBoundary }
  | { ok: false; reason: LassoRejection }

/**
 * The whole pipeline: raw freehand trail in, GeoJSON Polygon out.
 *
 * Reports a reason rather than just null, because the two failures deserve different
 * treatment: a stray tap should vanish silently, while a loop that crossed itself needs to be
 * explained or the user has no idea what to do differently.
 */
export function pathToPolygon(
  rawPoints: LngLat[],
  tolerance: number = SIMPLIFY_TOLERANCE,
): LassoResult {
  const deduped = dropConsecutiveDuplicates(rawPoints)
  if (deduped.length < 3) return { ok: false, reason: 'too-small' }

  const simplified = dropConsecutiveDuplicates(simplifyPath(deduped, tolerance))

  // Simplification can collapse a near-straight scribble to 2 points, which is not an area.
  if (simplified.length < 3) return { ok: false, reason: 'too-small' }

  // closeRing adds the repeated position, so a real triangle is 4 entries.
  if (closeRing(simplified).length < 4)
    return { ok: false, reason: 'too-small' }

  // Checked AFTER simplification, because that is the ring the server will see -- and because
  // Douglas-Peucker can in principle introduce a crossing that the raw path did not have.
  //
  // A crossing is repaired rather than refused where it can be: almost every one is an
  // overshoot at the end of the gesture, and asking someone to draw the whole territory again
  // because the last few millimetres strayed is the kind of thing that makes a tool feel
  // hostile. Only a shape that still crosses itself after the tail is given back is rejected.
  const ring = trimTailToSimpleRing(simplified)

  if (!ring) return { ok: false, reason: 'crosses-itself' }

  return { ok: true, boundary: { type: 'Polygon', coordinates: [ring] } }
}
